import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter,
  Input, Output, inject, OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { LocalizationPipe } from '@abp/ng.core';
import { NgxValidateCoreModule } from '@ngx-validate/core';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { SelectModule } from 'primeng/select';
import { SelectButtonModule } from 'primeng/selectbutton';
import { MultiSelectModule } from 'primeng/multiselect';
import { FormActionsComponent } from 'src/core/forms/form-actions/form-actions.component';
import { AgentLookupDto } from 'src/core/models/agent/agent-lookup.dto';
import { AgentService } from 'src/core/services/agent/agent.service';
import { AgentRoutingMethod } from '../../models/agent-routing-method.enum';
import { CallPriority } from '../../models/call-priority.enum';
import { QueuePriority } from '../../models/queue-priority.enum';
import { HoldMusicType } from '../../models/hold-music-type.enum';
import { AnnouncementLanguage } from '../../models/announcement-language.enum';
import { QueueUserDto } from '../../models/queue-user.dto';
import { QueueDto } from '../../models/queue.dto';
import {
  DEFAULT_SLA_TARGET_SECONDS,
  MIN_SLA_TARGET_SECONDS,
} from '../../models/queue-sla.constant';
import { QueueService } from '../../services/queue.service';
import { WrapUpDefinitionDto } from 'src/core/models/wrap-up-definition/wrap-up-definition.dto';
import { CrudService } from 'src/core/services/crud/crud.service';
import {
  LocalizationParamWithArgs,
  ToasterService,
} from 'src/core/services/toaster/toaster.service';
import { ConfirmationService } from 'src/core/services/confirmation/confirmation.service';
import { FeatureService } from 'src/core/services/feature/feature.service';
import { FeatureConstants } from 'src/core/constants/feature.constant';
import { Confirmation } from '@abp/ng.theme.shared';
import { Observable, finalize, Subject, takeUntil } from 'rxjs';
import { DeclineReasonDto } from 'src/core/models/decline-reason/decline-reason.dto';
import { ListRequestDto } from 'src/core/models/request/list-request.dto';
import { ListResponseDto } from 'src/core/models/request/list-response.dto';
import { CxUserAvatarComponent } from 'src/app/shared/components/cx-user-avatar/cx-user-avatar.component';

@Component({
  selector: 'app-queue-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    LocalizationPipe,
    InputTextModule,
    TextareaModule,
    ToggleSwitchModule,
    SelectModule,
    SelectButtonModule,
    MultiSelectModule,
    NgxValidateCoreModule,
    FormActionsComponent,
    CxUserAvatarComponent,
  ],
  templateUrl: './queue-form.component.html',
  styleUrl: './queue-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QueueFormComponent implements OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly crudService = inject(CrudService);
  private readonly agentService = inject(AgentService);
  private readonly queueService = inject(QueueService);
  private readonly toasterService = inject(ToasterService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly featureService = inject(FeatureService);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly HoldMusicType = HoldMusicType;
  private readonly holdMusicMaxSizeBytes = 50 * 1024 * 1024;
  private readonly holdMusicMaxDurationSeconds = 600;
  private readonly holdMusicAllowedExtensions = ['.wav'];
  readonly holdMusicAccept = '.wav,audio/wav';

  selectedHoldMusicFile: File | null = null;
  holdMusicFileName: string | null = null;

  @Input() queueId: number | null = null;
  @Output() saved = new EventEmitter<void>();
  @Output() cancelled = new EventEmitter<void>();

  readonly queuePriorityOptions = [
    { label: 'Queue::QueuePriority:Low', value: QueuePriority.Low },
    { label: 'Queue::QueuePriority:Medium', value: QueuePriority.Medium },
    { label: 'Queue::QueuePriority:High', value: QueuePriority.High },
  ];

  /**
   * Whether this tenant may use queue announcements at all. When off the whole section is hidden and
   * the stored values are left untouched, so switching the feature back on restores what was configured
   * rather than a default.
   */
  readonly isAnnouncementsEnabled = this.featureService.isEnabled(FeatureConstants.queueAnnouncements);

  readonly announcementLanguageOptions = [
    { label: 'Settings::Queues:Announcements:Language:Turkish', value: AnnouncementLanguage.Turkish },
    { label: 'Settings::Queues:Announcements:Language:English', value: AnnouncementLanguage.English },
  ];

  readonly slaTargetMin = MIN_SLA_TARGET_SECONDS;
  private readonly slaTargetDefault = DEFAULT_SLA_TARGET_SECONDS;
  private readonly maxResponseTimeMax = 120;

  readonly slaTargetValidationBlueprints = {
    required: `Settings::Queues:SlaTarget:MinValidationError[${this.slaTargetMin}]`,
    min: 'Settings::Queues:SlaTarget:MinValidationError[{{ min }}]',
    slaTargetNotWholeSeconds: 'Settings::Queues:SlaTarget:IntegerValidationError',
    slaTargetAboveMaxResponseTime:
      'Settings::Queues:SlaTarget:MaxValidationError[{{ maxResponseTime }}]',
  };

  /** Shared range of both announcement interval fields; the backend validator enforces the same bounds. */
  readonly announcementIntervalMin = 10;
  readonly announcementIntervalMax = 300;
  private readonly announcementIntervalDefault = 60;

  form!: FormGroup;
  isSubmitting = false;
  availableAgents: AgentLookupDto[] = [];
  assignedUsers: AgentLookupDto[] = [];
  selectedAgentId: string | null = null;
  loadingAgents = false;
  availableDeclineReasons: DeclineReasonDto[] = [];
  selectedDeclineReasonIds: number[] = [];

  availableWrapUpCodes: WrapUpDefinitionDto[] = [];
  loadingWrapUpCodes = false;
  private destroy$ = new Subject<void>();

  get isEditMode(): boolean {
    return this.queueId != null && this.queueId > 0;
  }

  get priorityDetailsKey(): string {
    switch (this.form?.get('priority')?.value) {
      case QueuePriority.Low:
        return 'Queue::QueuePriority:Details:Low';
      case QueuePriority.High:
        return 'Queue::QueuePriority:Details:High';
      case QueuePriority.Medium:
      default:
        return 'Queue::QueuePriority:Details:Medium';
    }
  }

  private get slaTargetError(): LocalizationParamWithArgs | null {
    const control = this.form?.get('slaTargetSeconds');

    if (control == null || control.valid) {
      return null;
    }

    const aboveMax = control.getError('slaTargetAboveMaxResponseTime');
    if (aboveMax) {
      return {
        key: 'Settings::Queues:SlaTarget:MaxValidationError',
        params: [aboveMax.maxResponseTime],
      };
    }

    if (control.hasError('slaTargetNotWholeSeconds')) {
      return { key: 'Settings::Queues:SlaTarget:IntegerValidationError', params: [] };
    }

    return { key: 'Settings::Queues:SlaTarget:MinValidationError', params: [this.slaTargetMin] };
  }

  get isAnnounceQueuePositionEnabled(): boolean {
    return this.form?.get('announceQueuePosition')?.value === true;
  }

  get isAnnounceEstimatedWaitTimeEnabled(): boolean {
    return this.form?.get('announceEstimatedWaitTime')?.value === true;
  }

  /**
   * Whether the queue announces anything at all, which is what the shared announcement interval is
   * governed by - the backend throttles a caller's whole round on that single value, so it stays
   * editable while either announcement is on.
   */
  get isAnyAnnouncementEnabled(): boolean {
    return this.isAnnounceQueuePositionEnabled || this.isAnnounceEstimatedWaitTimeEnabled;
  }

  get positionPreviewKey(): string {
    return `Settings::Queues:Announcements:Preview:Position:${this.announcementLanguageCode}`;
  }

  get waitTimePreviewKey(): string {
    return `Settings::Queues:Announcements:Preview:WaitTime:${this.announcementLanguageCode}`;
  }

  private get announcementLanguageCode(): 'tr' | 'en' {
    return this.form?.get('announcementLanguage')?.value === AnnouncementLanguage.English ? 'en' : 'tr';
  }

  ngOnInit(): void {
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(200)]],
      code: [null as string | null, [Validators.maxLength(50)]],
      description: [null as string | null],
      isActive: [true],
      priority: [QueuePriority.Medium],
      agentRoutingMethod: [AgentRoutingMethod.LongestIdleTime],
      callPriority: [CallPriority.Fifo],
      wrapUpCodeIds: [[] as number[]],
      maxResponseTime: [15, [Validators.required, Validators.min(10), Validators.max(this.maxResponseTimeMax)]],
      slaTargetSeconds: [
        this.slaTargetDefault,
        [
          Validators.required,
          Validators.min(this.slaTargetMin),
          this.wholeSeconds,
          this.slaTargetWithinMaxResponseTime,
        ],
      ],
      holdMusicType: [HoldMusicType.Default],
      announcementLanguage: [AnnouncementLanguage.Turkish],
      announceQueuePosition: [false],
      announcementFrequencySeconds: [
        this.announcementIntervalDefault,
        [Validators.required, Validators.min(this.announcementIntervalMin), Validators.max(this.announcementIntervalMax)],
      ],
      announceEstimatedWaitTime: [false],
      estimatedWaitTimeRoundToSeconds: [
        this.announcementIntervalDefault,
        [Validators.required, Validators.min(this.announcementIntervalMin), Validators.max(this.announcementIntervalMax)],
      ],
    });

    this.watchAnnouncementToggles();
    this.watchMaxResponseTime();

    this.form.controls.code.disable();

    this.loadAgentUsers();
    this.loadDeclineReasons();
    this.loadWrapUpCodes();

    if (this.isEditMode) {
      this.loadQueue();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  addAgent(): void {
    if (!this.selectedAgentId) return;
    const agent = this.availableAgents.find(a => a.id === this.selectedAgentId);
    if (agent && !this.assignedUsers.some(a => a.id === agent.id)) {
      this.assignedUsers = [...this.assignedUsers, agent];
      this.selectedAgentId = null;
    }
  }

  removeAgent(agentId: string): void {
    if (this.assignedUsers.length === 1) {
      this.confirmationService
        .warn('Settings::Queues:LastAgentMessage', 'Settings::Queues:LastAgentTitle')
        .subscribe(status => {
          if (status === Confirmation.Status.confirm) {
            this.assignedUsers = this.assignedUsers.filter(a => a.id !== agentId);
            this.cdr.markForCheck();
          }
        });
      return;
    }

    this.assignedUsers = this.assignedUsers.filter(a => a.id !== agentId);
  }

  get unassignedUsers(): AgentLookupDto[] {
    const assignedIds = new Set(this.assignedUsers.map(a => a.id));
    return this.availableAgents.filter(a => !assignedIds.has(a.id));
  }

  onSave(): void {
    if (this.isSubmitting) return;

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      if (this.form.get('maxResponseTime')?.invalid) {
        this.toasterService.error('Settings::Queues:MaxResponseTime:ValidationError');
      }
      const slaTargetError = this.slaTargetError;
      if (slaTargetError) {
        this.toasterService.error(slaTargetError);
      }
      if (this.form.get('announcementFrequencySeconds')?.invalid) {
        this.toasterService.error('Settings::Queues:Announcements:Frequency:ValidationError');
      }
      if (this.form.get('estimatedWaitTimeRoundToSeconds')?.invalid) {
        this.toasterService.error('Settings::Queues:Announcements:RoundTo:ValidationError');
      }
      this.cdr.markForCheck();
      return;
    }

    this.isSubmitting = true;
    const value = this.form.getRawValue();
    const payload: any = {
      id: this.queueId,
      name: value.name.trim(),
      code: value.code?.trim() || null,
      description: value.description?.trim() || null,
      isActive: value.isActive,
      priority: value.priority,
      maxResponseTime: value.maxResponseTime,
      slaTargetSeconds: value.slaTargetSeconds,
      holdMusicType: value.holdMusicType,
      announcementLanguage: value.announcementLanguage,
      announceQueuePosition: value.announceQueuePosition,
      announcementFrequencySeconds: value.announcementFrequencySeconds,
      announceEstimatedWaitTime: value.announceEstimatedWaitTime,
      estimatedWaitTimeRoundToSeconds: value.estimatedWaitTimeRoundToSeconds,
      assignedUsers: this.assignedUsers.map(
        agent => ({
          queueId: this.queueId ?? 0,
          agentId: agent.id,
          userId: agent.id,
        }),
      ),
      declineReasonIds: [...this.selectedDeclineReasonIds],
      wrapUpCodeIds: value.wrapUpCodeIds ?? [],
    };

    const request$ = this.isEditMode
      ? this.crudService.update<QueueDto>(QueueDto, { ...payload, id: this.queueId } as any)
      : this.crudService.insertWithoutId<QueueDto>(QueueDto, payload as any);

    this.submitQueue(request$);
  }

  onCancel(): void {
    this.cancelled.emit();
  }

  getAgentDisplayName(agent: AgentLookupDto): string {
    return agent.name ? `${agent.name} (${agent.userName})` : agent.userName;
  }

  get isCustomHoldMusic(): boolean {
    return this.form?.get('holdMusicType')?.value === HoldMusicType.Custom;
  }

  onHoldMusicTypeChange(): void {
    if (!this.isCustomHoldMusic) {
      this.clearHoldMusicSelection();
    }
    this.cdr.markForCheck();
  }

  onHoldMusicFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;

    input.value = '';

    if (!file) {
      return;
    }

    const name = file.name.toLowerCase();
    const hasAllowedExtension = this.holdMusicAllowedExtensions.some(ext => name.endsWith(ext));
    if (!hasAllowedExtension) {
      this.toasterService.error('Settings::Queues:HoldMusic:InvalidFormat');
      return;
    }

    if (file.size > this.holdMusicMaxSizeBytes) {
      this.toasterService.error('Settings::Queues:HoldMusic:FileTooLarge');
      return;
    }

    this.measureAudioDuration(file)
      .then(duration => {
        if (duration > this.holdMusicMaxDurationSeconds) {
          this.toasterService.error('Settings::Queues:HoldMusic:DurationTooLong');
          return;
        }
        this.selectedHoldMusicFile = file;
        this.holdMusicFileName = file.name;
        this.cdr.markForCheck();
      })
      .catch(() => {
        this.toasterService.error('Settings::Queues:HoldMusic:UnreadableFile');
      });
  }

  removeHoldMusicFile(): void {
    this.clearHoldMusicSelection();
    this.cdr.markForCheck();
  }

 
  private wholeSeconds(control: AbstractControl): ValidationErrors | null {
    const value = control.value;

    return value == null || Number.isInteger(value) ? null : { slaTargetNotWholeSeconds: true };
  }

  private slaTargetWithinMaxResponseTime(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    const maxResponseTime = control.parent?.get('maxResponseTime')?.value;

    if (typeof value !== 'number' || typeof maxResponseTime !== 'number' || maxResponseTime <= 0) {
      return null;
    }

    return value > maxResponseTime ? { slaTargetAboveMaxResponseTime: { maxResponseTime } } : null;
  }

  private watchMaxResponseTime(): void {
    this.form
      .get('maxResponseTime')!
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const slaTarget = this.form.get('slaTargetSeconds')!;

        slaTarget.markAsDirty();
        slaTarget.updateValueAndValidity();

        this.cdr.markForCheck();
      });
  }

  private watchAnnouncementToggles(): void {
    for (const toggleName of ['announceQueuePosition', 'announceEstimatedWaitTime']) {
      this.form
        .get(toggleName)!
        .valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          // Repair whatever the operator can no longer reach. The controls keep their always-on
          // required/min/max validators while hidden, so an interval left mid-edit would block the
          // save with no visible field to fix it in.
          this.resetIfHiddenAndInvalid('announcementFrequencySeconds', this.isAnyAnnouncementEnabled);
          this.resetIfHiddenAndInvalid(
            'estimatedWaitTimeRoundToSeconds',
            this.isAnnounceEstimatedWaitTimeEnabled);

          this.cdr.markForCheck();
        });
    }

    this.form
      .get('announcementLanguage')!
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => this.cdr.markForCheck());
  }

  /**
   * Restores an interval control's default once it is both hidden and invalid.
   *
   * The frequency is deliberately keyed off "any announcement is on" rather than off one toggle: it is
   * shared, so turning the position announcement off while the wait time is still announced must leave
   * it alone.
   */
  private resetIfHiddenAndInvalid(controlName: string, isVisible: boolean): void {
    const control = this.form.get(controlName)!;

    if (!isVisible && control.invalid) {
      control.setValue(this.announcementIntervalDefault);
    }
  }

  private clearHoldMusicSelection(): void {
    this.selectedHoldMusicFile = null;
    this.holdMusicFileName = null;
  }

  private async measureAudioDuration(file: File): Promise<number> {
    const arrayBuffer = await file.arrayBuffer();
    const AudioContextCtor =
      window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const audioContext = new AudioContextCtor();
    try {
      const buffer = await audioContext.decodeAudioData(arrayBuffer);
      return buffer.duration;
    } finally {
      void audioContext.close();
    }
  }

  private loadDeclineReasons(): void {
    const request: ListRequestDto = {
      skipCount: 0,
      maxResultCount: 1000,
      filters: [],
      sorters: [],
    };

    this.crudService.get<DeclineReasonDto>(DeclineReasonDto, request).subscribe({
      next: (response: ListResponseDto<DeclineReasonDto>) => {
        this.availableDeclineReasons = response.items ?? [];
        this.cdr.markForCheck();
      },
    });
  }

  private loadAgentUsers(): void {
    this.loadingAgents = true;
    this.agentService.getAgentLookup().subscribe({
      next: agents => {
        this.availableAgents = agents;
        this.loadingAgents = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.loadingAgents = false;
        this.cdr.markForCheck();
      },
    });
  }

  private loadQueue(): void {
    this.crudService.getById<QueueDto>(QueueDto, this.queueId!).subscribe({
      next: queue => {
        this.form.patchValue({
          name: queue.name,
          code: queue.code,
          description: queue.description,
          isActive: queue.isActive,
          priority: queue.priority ?? QueuePriority.Medium,
          agentRoutingMethod: queue.agentRoutingMethod,
          callPriority: queue.callPriority,
          wrapUpCodeIds: queue.wrapUpCodeIds ?? [],
          maxResponseTime: queue.maxResponseTime ?? 15,
          slaTargetSeconds: queue.slaTargetSeconds ?? this.slaTargetDefault,
          holdMusicType: queue.holdMusicType ?? HoldMusicType.Default,
          announcementLanguage: queue.announcementLanguage ?? AnnouncementLanguage.Turkish,
          announceQueuePosition: queue.announceQueuePosition ?? false,
          announcementFrequencySeconds: queue.announcementFrequencySeconds ?? this.announcementIntervalDefault,
          announceEstimatedWaitTime: queue.announceEstimatedWaitTime ?? false,
          estimatedWaitTimeRoundToSeconds:
            queue.estimatedWaitTimeRoundToSeconds ?? this.announcementIntervalDefault,
        });
        if (queue.holdMusicType === HoldMusicType.Custom && queue.holdMusicFileName) {
          this.holdMusicFileName = queue.holdMusicFileName;
        }
        this.selectedDeclineReasonIds = [...(queue.declineReasonIds ?? [])];
        const assignedUserIds = this.getAssignedAgentIds(queue.assignedUsers);
        if (assignedUserIds.length) {
          this.assignedUsers = this.availableAgents.filter(a => assignedUserIds.includes(a.id));
          if (this.assignedUsers.length === 0) {
            this.agentService.getAgentLookup().subscribe(agents => {
              this.availableAgents = agents;
              this.assignedUsers = agents.filter(a => assignedUserIds.includes(a.id));
              this.cdr.markForCheck();
            });
          }
        }
        this.cdr.markForCheck();
      },
    });
  }

  private loadWrapUpCodes(): void {
    if (this.availableWrapUpCodes.length > 0) {
      return;
    }

    this.loadingWrapUpCodes = true;
    this.crudService
      .get<WrapUpDefinitionDto>(WrapUpDefinitionDto, {
        maxResultCount: 1000,
      } as any)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.availableWrapUpCodes = (response as any).items ?? [];
          this.loadingWrapUpCodes = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.loadingWrapUpCodes = false;
          this.cdr.markForCheck();
        },
      });
  }

  private getAssignedAgentIds(assignedUsers: QueueUserDto[] | undefined): string[] {
    if (!assignedUsers?.length) {
      return [];
    }

    return assignedUsers
      .map(x => x.userId ?? (x as any).agentId ?? '')
      .filter((id): id is string => id.length > 0);
  }

  private submitQueue(request$: Observable<unknown>): void {
    let settled = false;

    request$.pipe(takeUntil(this.destroy$)).subscribe({
      next: response => {
        settled = true;
        const savedId = this.resolveSavedQueueId(response);
        const shouldUpload =
          this.form.get('holdMusicType')?.value === HoldMusicType.Custom &&
          this.selectedHoldMusicFile != null;

        if (shouldUpload && savedId != null) {
          this.uploadHoldMusic(savedId);
          return;
        }

        this.finishSubmit();
      },
      error: () => {
        settled = true;
        this.isSubmitting = false;
        this.cdr.markForCheck();
      },
      complete: () => {
        if (!settled) {
          this.isSubmitting = false;
          this.cdr.markForCheck();
        }
      },
    });
  }

  private uploadHoldMusic(queueId: number): void {
    this.queueService
      .uploadHoldMusic(queueId, this.selectedHoldMusicFile!)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isSubmitting = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: () => {
          this.toasterService.success('::SuccessfullySaved');
          this.saved.emit();
        },
        error: (err: HttpErrorResponse) => {
          this.toasterService.error(
            err?.error?.error?.message ?? 'Settings::Queues:HoldMusic:UnreadableFile',
          );
        },
      });
  }

  private finishSubmit(): void {
    this.isSubmitting = false;
    this.toasterService.success('::SuccessfullySaved');
    this.saved.emit();
    this.cdr.markForCheck();
  }

  private resolveSavedQueueId(response: unknown): number | null {
    if (this.isEditMode) {
      return this.queueId;
    }
    const created = response as QueueDto | null;
    return created?.id ?? null;
  }
}
